import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { brand } from "@/data/mockData";
import { useListingById } from "@/lib/content";
import { formatPrice, formatSqft } from "@/lib/format";
import RichText from "@/components/RichText";

export default function ListingDetail() {
  const { id } = useParams();
  const { data: listing, loading } = useListingById(id);
  const [active, setActive] = useState(0);

  if (loading && !listing) {
    return (
      <section className="bg-white pt-20 pb-24 min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-400 text-sm">
          Loading…
        </div>
      </section>
    );
  }

  if (!listing) {
    return (
      <section className="bg-white pt-20 pb-24 min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif text-4xl text-[#0a2540] mb-4">Listing not found</h1>
          <p className="text-slate-600 mb-6">This listing may have been sold or removed.</p>
          <Link to="/listings" className="inline-flex items-center gap-2 text-[#0a2540] link-underline">
            <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" /> Back to all listings
          </Link>
        </div>
      </section>
    );
  }

  const gallery = listing.images && listing.images.length > 0 ? listing.images : [listing.image].filter(Boolean);
  const main = gallery[active] || gallery[0];

  return (
    <>
      <SEO
        title={`${listing.title} — Johnny Tan`}
        description={listing.description}
      />

      <section className="bg-white pt-20 pb-8" data-testid={`listing-detail-${listing.id}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/listings" className="text-xs uppercase tracking-[0.25em] text-[#0a2540]/70 link-underline inline-flex items-center gap-2 mb-6">
            <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden="true" /> Back to Listings
          </Link>
          <div className="grid lg:grid-cols-[1.6fr,1fr] gap-10">
            {/* Gallery */}
            <div>
              <motion.div
                key={main}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.35 }}
                className="aspect-[4/3] overflow-hidden bg-gray-100 rounded-2xl mb-3"
              >
                <img src={main} alt={listing.title} className="w-full h-full object-cover" />
              </motion.div>
              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {gallery.slice(0, 5).map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className={`aspect-square overflow-hidden rounded-lg ${active === i ? "ring-2 ring-[#ffde59]" : "opacity-80 hover:opacity-100"}`}
                      data-testid={`listing-gallery-${i}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <Reveal>
              <div className="inline-block bg-[#fffbea] text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full mb-4">
                {listing.type}
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#0a2540] leading-tight mb-3">
                {listing.title}
              </h1>
              <p className="text-sm text-slate-500 flex items-center gap-2 mb-6">
                <i className="fa-solid fa-location-dot text-[#0a2540]" aria-hidden="true" /> {listing.location}
              </p>
              <div className="grid grid-cols-3 gap-4 py-5 my-5 border-y border-gray-100 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Beds</div>
                  <div className="font-serif text-xl text-[#0a2540]">{listing.bedrooms}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Baths</div>
                  <div className="font-serif text-xl text-[#0a2540]">{listing.bathrooms}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Size</div>
                  <div className="font-serif text-xl text-[#0a2540]">{formatSqft(listing.size)}</div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Price</div>
              <div className="font-serif text-3xl text-[#0a2540] mb-6">{formatPrice(listing.price)}</div>

              <div className="flex flex-wrap gap-3 mb-8">
                <a
                  href={`${brand.whatsapp}?text=Hi%20Johnny%2C%20I%27d%20like%20to%20ask%20about%20${encodeURIComponent(listing.title)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`listing-detail-whatsapp-${listing.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#ffde59] text-[#023e8a] px-5 py-3 text-sm font-semibold rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all w-full sm:w-auto"
                >
                  <i className="fa-brands fa-whatsapp" aria-hidden="true" /> WhatsApp Johnny
                </a>
                {listing.videoLink && listing.videoLink !== "#" && (
                  <a
                    href={listing.videoLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-3 text-sm font-semibold rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all w-full sm:w-auto"
                  >
                    <i className="fa-brands fa-youtube" aria-hidden="true" /> View Home Tour
                  </a>
                )}
                {listing.externalLink && listing.externalLink !== "#" && (
                  <a
                    href={listing.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-[#0a2540]/20 text-[#0a2540] px-5 py-3 text-sm font-medium rounded-full hover:border-[#0a2540] transition-all w-full sm:w-auto"
                  >
                    View on Portal <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                  </a>
                )}
              </div>

              <RichText html={listing.description} className="text-sm text-slate-600 leading-relaxed mb-7" />
            </Reveal>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
